import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { agentChannels, type AgentSummary } from '@eairlines/contracts';
import { getAgent, getAgentRegistry } from '../api/client';
import { Icons } from '../components/icons';

const kindLabels: Record<AgentSummary['kind'], string> = {
  ORCHESTRATOR: 'Orchestrator component',
  AGENT: 'Agent',
  SUB_AGENT: 'Sub-agent',
};

const channelLabels: Record<(typeof agentChannels)[number], string> = {
  web: 'Web',
  whatsapp: 'WhatsApp',
  ivr: 'Voice (IVR)',
};

type ChannelFilter = 'all' | (typeof agentChannels)[number];

export function AgentsPage() {
  const registry = useQuery({ queryKey: ['agents'], queryFn: getAgentRegistry });
  const [selectedKey, setSelectedKey] = useState<string>();
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState<ChannelFilter>('all');

  const agents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (registry.data?.agents ?? []).filter(
      (agent) =>
        (channel === 'all' || agent.channels.includes(channel)) &&
        (!term || agent.name.toLowerCase().includes(term) || agent.key.includes(term)),
    );
  }, [registry.data, search, channel]);
  const runtimes = useMemo(() => [...new Set(agents.map((agent) => agent.runtime))], [agents]);
  const effectiveKey = selectedKey ?? agents[0]?.key;
  const detail = useQuery({
    queryKey: ['agent', effectiveKey],
    queryFn: () => getAgent(effectiveKey!),
    enabled: Boolean(effectiveKey),
  });
  const lastImport = registry.data?.lastImport;

  return (
    <main className="feature-page">
      <section className="page-heading">
        <div>
          <h1>Agents</h1>
          <p>
            The conversational agents discovered in the platform source. Tone, guardrails and
            business rules are configured for these agents.
          </p>
        </div>
      </section>

      {registry.isError ? (
        <section className="error-state" role="alert">
          <strong>Agents could not be loaded</strong>
          <p>{registry.error.message}</p>
        </section>
      ) : null}
      {registry.isPending ? <div className="loading">Loading agent registry…</div> : null}

      {registry.data ? (
        <div className="guardrail-grid">
          <section className="panel list-panel agent-panel" aria-label="Agent list">
            <div className="panel-title-row">
              <h2>Agents ({agents.length})</h2>
              <select
                aria-label="Channel"
                value={channel}
                onChange={(event) => setChannel(event.target.value as ChannelFilter)}
              >
                <option value="all">All channels</option>
                {agentChannels.map((value) => (
                  <option key={value} value={value}>
                    {channelLabels[value]}
                  </option>
                ))}
              </select>
            </div>
            <div className="agent-search">
              <input
                type="search"
                aria-label="Search agents"
                placeholder="Search agents"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {runtimes.map((runtime) => (
              <div key={runtime} className="guardrail-list">
                <h3 className="agent-runtime">{runtime}</h3>
                {agents
                  .filter((agent) => agent.runtime === runtime)
                  .map((agent) => (
                    <button
                      key={agent.key}
                      className={
                        effectiveKey === agent.key ? 'guardrail-item selected' : 'guardrail-item'
                      }
                      onClick={() => setSelectedKey(agent.key)}
                    >
                      <span className={agent.parentKey ? 'agent-icon nested' : 'agent-icon'}>
                        <Icons.Bot size={18} />
                      </span>
                      <span className="guardrail-copy">
                        <strong>{agent.name}</strong>
                        <span className="badges">
                          <span className="badge">{kindLabels[agent.kind]}</span>
                          {agent.channels.map((value) => (
                            <span key={value} className="badge">
                              {channelLabels[value]}
                            </span>
                          ))}
                          {agent.status === 'RETIRED' ? (
                            <span className="badge retired">Retired</span>
                          ) : null}
                        </span>
                      </span>
                      <span className="agent-count">{agent.actionCount} actions</span>
                      <Icons.ChevronRight size={16} />
                    </button>
                  ))}
              </div>
            ))}
            {agents.length === 0 ? (
              <p className="panel-note">No agents match these filters.</p>
            ) : null}
          </section>

          {detail.isPending && effectiveKey ? (
            <section className="panel loading">Loading agent…</section>
          ) : null}
          {detail.isError ? (
            <section className="error-state" role="alert">
              <strong>Agent details could not be loaded</strong>
              <p>{detail.error.message}</p>
            </section>
          ) : null}
          {detail.data ? (
            <section className="panel agent-detail" aria-label="Agent details">
              <div className="panel-title-row">
                <div>
                  <h2>{detail.data.name}</h2>
                  <span className="badge">{kindLabels[detail.data.kind]}</span>
                </div>
              </div>
              <p className="agent-description">{detail.data.description}</p>
              <Link className="button" to={`/agents/${encodeURIComponent(detail.data.id)}/configuration`}>
                Configure agent
              </Link>
              <dl className="agent-facts">
                <dt>Key</dt>
                <dd>
                  <code>{detail.data.key}</code>
                </dd>
                <dt>Runtime</dt>
                <dd>{detail.data.runtime}</dd>
                <dt>Implementation</dt>
                <dd>
                  <code>
                    {detail.data.repository}: {detail.data.implementation}
                  </code>
                </dd>
                {detail.data.parentKey ? (
                  <>
                    <dt>Part of</dt>
                    <dd>
                      <button
                        className="link-button"
                        onClick={() => setSelectedKey(detail.data.parentKey!)}
                      >
                        {detail.data.parentKey}
                      </button>
                    </dd>
                  </>
                ) : null}
                <dt>Channels</dt>
                <dd>
                  {detail.data.channels.map((value) => channelLabels[value]).join(', ') || 'None'}
                </dd>
              </dl>
              <AgentList title="Routing intents" items={detail.data.intents} />
              <AgentList
                title={`Actions (${detail.data.actions.length})`}
                items={detail.data.actions.map((action) =>
                  action.via === 'agent' ? action.key : `${action.key} — via ${action.via}`,
                )}
              />
              <AgentList title="Prompts" items={detail.data.prompts} />
              {detail.data.children.length ? (
                <>
                  <h3>Sub-agents ({detail.data.children.length})</h3>
                  <div className="tag-row">
                    {detail.data.children.map((child) => (
                      <button
                        key={child.key}
                        className="chip"
                        onClick={() => setSelectedKey(child.key)}
                      >
                        {child.name}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </section>
          ) : null}

          <section className="panel" aria-label="Runtime facts and source">
            <div className="panel-title-row">
              <h2>Runtime facts ({registry.data.facts.length})</h2>
            </div>
            <p className="panel-note">Business rule conditions can use these facts.</p>
            <ul className="agent-fact-list">
              {registry.data.facts.map((fact) => (
                <li key={fact.key} title={fact.source}>
                  <code>{fact.key}</code> <span className="badge">{fact.type}</span>
                  <span>{fact.description}</span>
                </li>
              ))}
            </ul>
            {lastImport ? (
              <>
                <h3>Discovered from</h3>
                <ul className="agent-fact-list">
                  {lastImport.repositories.map((repository) => (
                    <li key={repository.repository}>
                      <code>{repository.repository}</code>
                      <span>
                        {repository.commit.slice(0, 8)} · {repository.commitDate}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="panel-note">
                  Imported {new Date(lastImport.importedAt).toLocaleString()} by{' '}
                  {lastImport.importedBy}.
                </p>
              </>
            ) : null}
          </section>
        </div>
      ) : null}
    </main>
  );
}

function AgentList({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <>
      <h3>{title}</h3>
      <ul className="agent-list">
        {items.map((item) => (
          <li key={item}>
            <code>{item}</code>
          </li>
        ))}
      </ul>
    </>
  );
}
